import * as React from "react";
import './SkipQuestionnaireModal.css'
import * as http from "../../../utils/http"
import * as translator from "../../../utils/translator";
import { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import SubmitButton from "../../SubmitButton/SubmitButton";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { useCartContext } from "../../../customHooks/useCart";
import { useCustomer } from "../../../customHooks/useCustomer";
import { track as trackGTM } from "../../../PixelTrackers/GtmTracker";
import {
  markPendingClear,
  markSkipQuestionnaireAnsweredThisSession,
  wasSkipQuestionnaireAnsweredThisSession,
} from "../../../utils/QuestionnaireAnswersStorage";

type Props = {
  language: string | undefined
  country: string | undefined
  skipModals: string | null
  categoryCatalog: { id: number, mention: string } | null
  /**
   * When true: same eligibility fetch + submit as Variant A, but no popup UI.
   * Variant B drives submit via ref from health_condition "No change".
   */
  headless?: boolean
  onHealthChanged?: () => void
  /** WL3: the Yes/No buttons turn orange instead of green on hover / press. */
  orangeButtons?: boolean
  /** Once answered (Yes, or a successful skip), don't show the popup again this tab session. */
  askOncePerSession?: boolean
  /** Skip-eligibility result: null while unknown (check in flight, or no customer/catalog yet). */
  onEligibilityChange?: (eligible: boolean | null) => void
}

/** Imperative API for Variant B headless use. */
export type SkipQuestionnaireHandle = {
  /**
   * Same path as the modal's "No, nothing changed" button.
   * Resolves true if navigation started; false if skip is unavailable.
   */
  submit: () => Promise<boolean>
}

export const SkipQuestionnaireModal = forwardRef<SkipQuestionnaireHandle, Props>(function SkipQuestionnaireModal(
  { language, country, skipModals, categoryCatalog, headless = false, onHealthChanged, orangeButtons = false, askOncePerSession = false, onEligibilityChange },
  ref
) {
  const { customerState } = useCustomer();
  const [showSkipQuestionnaireModal, setShowSkipQuestionnaireModal] = useState(0)
  // Kept in state so the payment-page auto-submit effect can react to it
  // (Variant A behavior). Payload for POST also lives in skipPayloadRef.
  const [questionnaire, setQuestionnaire] = useState('')
  const { saveCart } = useCartContext();
  const navigate = useNavigate();
  const [searchParamas] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const mention = categoryCatalog?.mention
  const categoryCatalogID = categoryCatalog?.id

  // Latest skip payload for submit (state + ref stay in sync).
  const skipPayloadRef = useRef<{
    questionnaire: any
    customerZipcode: string
    reusePhotoFromOrderID: number | null
  } | null>(null)
  // In-flight eligibility GET — submit waits on this so a fast tap still works.
  const eligibilityPromiseRef = useRef<Promise<boolean>>(Promise.resolve(false))

  const NO = "no";
  const YES = "yes";

  const trackSkipDecision = (term: 'yes' | 'no') => {
    trackGTM("custom", "ga4_auto_event", {
      'event_name': 'skip_questionnaire_popup',
      'funnel_step': 'funnel_step_2',
      'term': term,
      'subcategory': mention
    });
  };

  // Fetches skip-eligibility for the current customer + catalog and populates
  // skipPayloadRef. Shared by the effect (fires as soon as login/catalog is
  // known) and submit() (re-fetches on demand so a decision never depends on
  // the effect having already run — this is what makes both the Google-OAuth
  // reload path and the in-SPA sign-in path reliably reach select_treatment).
  const fetchEligibility = (): Promise<boolean> => {
    if (!customerState?.customerID || !categoryCatalogID || mention === "schengen") {
      skipPayloadRef.current = null
      eligibilityPromiseRef.current = Promise.resolve(false)
      onEligibilityChange?.(null)
      return eligibilityPromiseRef.current
    }

    onEligibilityChange?.(null)
    eligibilityPromiseRef.current = http
      .getFromServer(
        `skip_questionnaire/${mention}/${country}/${language}?category_catalog_id=${categoryCatalogID}`,
        [http.MIDDLEWARES.CUSTOMER_ID]
      )
      .then((response: any) => {
        if (response.data.matchHistoricPurchase) {
          if (!headless && !(askOncePerSession && wasSkipQuestionnaireAnsweredThisSession(categoryCatalogID))) {
            setShowSkipQuestionnaireModal(2)
          }
          MainTracker.track("custom", "start skip q'r")
          setQuestionnaire(response.data.questionnaire)
          skipPayloadRef.current = {
            questionnaire: response.data.questionnaire,
            customerZipcode: response.data.customerZipcode || '',
            reusePhotoFromOrderID: response.data.matchHistoricPurchase.first_order_id ?? null,
          }
          onEligibilityChange?.(true)
          return true
        }
        setQuestionnaire('')
        skipPayloadRef.current = null
        onEligibilityChange?.(false)
        return false
      })
      .catch((error) => {
        window.clientLogger.error("error get from server skip questionnaire", error)
        skipPayloadRef.current = null
        // A failed check is not a "no": stay unknown so the question keeps its retry on tap.
        onEligibilityChange?.(null)
        return false
      })

    return eligibilityPromiseRef.current
  }

  const submit = async (): Promise<boolean> => {
    // Wait for any in-flight eligibility fetch (headless fast-tap safety).
    await eligibilityPromiseRef.current
    // If nothing eligible resolved yet but the customer is now logged in
    // (e.g. submit() called on the same tick as login, before the effect
    // re-ran), fetch it now and await — so the decision uses live auth state.
    if (!skipPayloadRef.current?.questionnaire && customerState?.customerID && categoryCatalogID) {
      await fetchEligibility()
    }
    const payload = skipPayloadRef.current
    if (!payload?.questionnaire) return false

    const cleanedSearchParams = new URLSearchParams(searchParamas);
    const oauthParamsToRemove = [
      'accessToken',
      'lastName',
      'firstName',
      'refreshToken',
      'email',
      'language',
      'country',
      'showOauthAdditionalInfoModal',
      'provider'
    ];
    oauthParamsToRemove.forEach(param => {
      cleanedSearchParams.delete(param);
    });

    setIsLoading(true);
    try {
      const parsedQuestionnaire = typeof payload.questionnaire === 'string'
        ? JSON.parse(payload.questionnaire)
        : payload.questionnaire;

      const selectedConsultationType = country?.toUpperCase() === "CH" && mention?.includes("medical_cannabis")
        ? "video"
        : ((searchParamas.get("ct") as "video" | "text") || "text");

      const response = await http.postMultiPartsToServer(
        `questionnaire/${country}/${language}/${mention}?${cleanedSearchParams.toString()}`,
        {
          questionnaire: parsedQuestionnaire,
          mention,
          isBlocking: false,
          country,
          language,
          allowOnlyPrescription: searchParamas.get('came_from_out_of_stock_banner') === 'true' ? 1 : 0,
          isExpressReorder: false,
          selfPickUpPharmacyID: searchParamas.get('sppi'),
          zipcode: payload.customerZipcode,
          selectedConsultationType,
          customerVerificationRequired: searchParamas.get("cv") === 'true',
          reusePhotoFromOrderID: payload.reusePhotoFromOrderID,
        },
        [],
        [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER]
      )

      saveCart({
        id: response.data.cartID,
        token: response.data.cartToken,
        mention,
        mixpanel_title: response.data.catalog.mixpanel_title,
        products: [],
        timekitBooking: null,
        consultation_type: selectedConsultationType,
        is_follow_up_consultation: response.data.is_follow_up_consultation ? 1 : 0,
        consultation_fee: response.data.consultation_fee ?? undefined,
        customer_verification_type: response.data.customerVerificationType,
        should_id_verify: response.data.shouldIDVerify
      })

      if (categoryCatalogID && Number.isFinite(categoryCatalogID) && typeof country === "string") {
        markPendingClear(country, categoryCatalogID);
      }
      markSkipQuestionnaireAnsweredThisSession(categoryCatalogID, true);

      MainTracker.track("custom", "skipped q'r");
      const catalogSettings = JSON.parse(response.data.catalog.settings ?? '{}');
      const newSearchParams = cleanedSearchParams.toString()

      if (response.data.catalog && catalogSettings.skip_choose_treatment) {
        navigate({
          pathname: `/shipping/${country}/${language}`,
          search: newSearchParams ? `?${newSearchParams}` : ''
        });
        return true;
      }

      navigate({
        pathname: `/select_treatment/${country}/${language}/${mention}`,
        search: newSearchParams ? `?${newSearchParams}` : ''
      })
      return true;
    } catch (err) {
      window.clientLogger.error("error postToServer SkipQuestionnaireModal: ", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    submit: async () => {
      trackSkipDecision(YES);
      return submit();
    },
  }))

  // Same eligibility fetch as Variant A. Re-runs when login / catalog arrives
  // (needed for headless mid-flow login). submit() can also trigger this
  // on demand, so the two paths share fetchEligibility.
  useEffect(() => {
    fetchEligibility()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerState?.customerID, categoryCatalogID, mention, country, language, headless])

  // skip modals if user is coming from payment page
  useEffect(() => {
    if (skipModals && questionnaire) {
      submit();
    }
  }, [questionnaire]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showSkipQuestionnaireModal === 2) {
      MainTracker.track('pv', 'View Pop Up', { 'Pop Up Name': 'Has your health changed? NEW' });
    }
  }, [showSkipQuestionnaireModal]);

  if (headless) return null;

  if (!showSkipQuestionnaireModal) return null;

  const buttonClassName = orangeButtons ? 'skip-modal-button careplus-submit' : 'skip-modal-button';

  return (
    <div className="skip-modal-overlay">
      <div className="skip-modal-card">
        <div className="skip-modal-content">
          <h2 className="skip-modal-heading">
            {translator.translate(language, "default", "skip_questionnaire/health_changed_question")}
          </h2>
          <div className="skip-modal-disclaimer">
            <div className="skip-modal-disclaimer-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="9" cy="9" r="7.5" stroke="#4F94FA" strokeWidth="1.5" />
                <line x1="9" y1="6" x2="9" y2="9" stroke="#4F94FA" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="9" cy="12" r="0.75" fill="#4F94FA" />
              </svg>
            </div>

            <div className="skip-modal-disclaimer-text">
              {translator.translate(language, "default", "skip_questionnaire/disclaimer")}
            </div>
          </div>
          <div className="skip-modal-buttons">
            <SubmitButton
              onSubmit={() => {
                trackSkipDecision(YES);
                submit();
              }}
              placeholder={translator.translate(language, "default", "LoginQuestion/no")}
              className={buttonClassName}
              id={'skip-questionnaire-no-button'}
              loading={isLoading}
              language={language}
              loadingText={translator.translate(language, "default", "SubmitButton/verifying_text")}
            />
            <SubmitButton
              onSubmit={() => {
                trackSkipDecision(NO);
                markSkipQuestionnaireAnsweredThisSession(categoryCatalogID, false);
                setShowSkipQuestionnaireModal(0);
                onHealthChanged?.();
              }}
              placeholder={translator.translate(language, "default", "LoginQuestion/yes")}
              className={buttonClassName}
              id={'skip-questionnaire-yes-button'}
              language={language}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
